import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentStatus() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const bookingId = sp.get("booking_id");
  const purpose = sp.get("purpose"); // 'addons' or null
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0, alive = true;
    const poll = async () => {
      if (!alive) return;
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (_e) {
        if (attempts >= 8) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center">
      {status === "polling" && (<>
        <Loader2 className="w-12 h-12 mx-auto text-[var(--brand-primary)] animate-spin" />
        <h2 className="font-heading text-2xl font-semibold mt-4 text-[var(--brand-accent)]">Confirming your payment...</h2>
        <p className="text-slate-500 mt-2">Hang tight, this takes a moment.</p>
      </>)}
      {status === "paid" && (<>
        <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
        <h2 className="font-heading text-3xl font-bold mt-4 text-[var(--brand-accent)]" data-testid="payment-success-heading">
          {purpose === "addons" ? "Add-ons confirmed!" : "Payment Successful!"}
        </h2>
        {data?.ticketing_deferred && purpose !== "addons" ? (
          <div
            data-testid="ticketing-deferred-banner"
            className="mt-4 mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-5 text-left text-amber-900 text-sm leading-relaxed"
          >
            <div className="font-semibold text-amber-800 mb-1">Finalising your e-ticket…</div>
            Your payment is confirmed. We&apos;re finalising your e-ticket with the airline — you&apos;ll receive an email with the airline PNR and ticket number shortly. Most issues clear within a few minutes; if not, our team will reach out.
          </div>
        ) : (
          <p className="text-slate-600 mt-2">
            {purpose === "addons"
              ? "Your selected add-ons have been added to your booking."
              : "Your booking is confirmed. We've sent a confirmation to your email."}
          </p>
        )}
        <PaymentAmount data={data} />
        <div className="flex justify-center gap-3 mt-8">
          {purpose === "addons" && bookingId ? (
            <Button data-testid="goto-ticket-btn" className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]" onClick={() => navigate(`/bookings/${bookingId}`)}>View ticket</Button>
          ) : (
            <Button data-testid="goto-mybookings-btn" className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]" onClick={() => navigate("/my-bookings")}>View my bookings</Button>
          )}
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/")}>Back to home</Button>
        </div>
      </>)}
      {(status === "expired" || status === "timeout" || status === "error") && (<>
        <XCircle className="w-16 h-16 mx-auto text-red-500" />
        <h2 className="font-heading text-2xl font-semibold mt-4 text-[var(--brand-accent)]">Payment {status === "expired" ? "expired" : "could not be confirmed"}</h2>
        <p className="text-slate-500 mt-2">Please try again, or contact support if amount was deducted.</p>
        <Button className="mt-6 rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]" onClick={() => navigate(purpose === "addons" ? `/bookings/${bookingId}/addons` : `/checkout/${bookingId}`)}>Retry payment</Button>
      </>)}
    </div>
  );
}

function PaymentAmount({ data }) {
  const { format: fmt, currency: dispCcy } = useCurrency();
  const usd = data?.amount_total ? data.amount_total / 100 : 0;
  const stripeCcy = (data?.currency || "USD").toUpperCase();
  // Stripe always settles in USD on our account — convert to user's display
  // currency for visual consistency with the rest of the workflow.
  return (
    <div className="mt-3 inline-flex flex-col items-center gap-1">
      <div className="bg-teal-50 border border-teal-100 rounded-full px-4 py-1 text-sm text-teal-800">
        Amount: <strong className="font-mono">{fmt(usd)} {dispCcy}</strong>
      </div>
      {dispCcy !== "USD" && (
        <div className="text-[10px] text-slate-400">
          Charged via Stripe: ${usd.toFixed(2)} {stripeCcy}
        </div>
      )}
    </div>
  );
}
